import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Wallet, AlertTriangle, CalendarDays, Pencil, Activity } from "lucide-react";

const fmtCurrency = (n) =>
  typeof n === "number"
    ? n.toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 2 })
    : "—";

const fmtDate = (iso) => {
  if (!iso) return "—";
  const [y, m, d] = iso.split("-");
  return `${m}/${d}/${y}`;
};

const BurnChip = ({ tone, billed, authorized, billedPct }) => {
  if (tone === "unset") {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 text-[10px] uppercase tracking-wider font-semibold" data-testid="burn-chip-unset">
        Not configured
      </span>
    );
  }
  const cls =
    tone === "red" ? "bg-red-100 text-red-800 border-red-300" :
    tone === "amber" ? "bg-amber-100 text-amber-800 border-amber-300" :
    "bg-emerald-100 text-emerald-800 border-emerald-300";
  const label = tone === "red" ? "Overrun" : tone === "amber" ? "High burn" : "On track";
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[10px] uppercase tracking-wider font-semibold ${cls}`} data-testid={`burn-chip-${tone}`}>
      {label} · {billedPct}%
    </span>
  );
};

const CycleChip = ({ warning, days }) => {
  if (warning !== "red" || days === null) return null;
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-red-100 text-red-800 border border-red-300 text-[10px] uppercase tracking-wider font-semibold" data-testid="cycle-warning-chip">
      <AlertTriangle className="w-3 h-3" />
      {days < 0 ? `${Math.abs(days)}d past` : `${days}d to renewal`}
    </span>
  );
};

const ProgressBar = ({ pct, tone }) => {
  const color =
    tone === "red" ? "bg-red-500" :
    tone === "amber" ? "bg-amber-500" :
    tone === "green" ? "bg-emerald-500" :
    "bg-slate-300";
  return (
    <div className="h-2 bg-secondary rounded-full overflow-hidden">
      <div className={`h-full ${color} transition-all`} style={{ width: `${Math.min(100, pct)}%` }} />
    </div>
  );
};

export default function AdminBudgetTracking() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/admin/budget-tracking")
      .then(({ data }) => setData(data))
      .finally(() => setLoading(false));
  }, []);

  const rows = data?.rows || [];
  const configured = rows.filter((r) => r.burn_tone !== "unset");
  const totalAuthorized = configured.reduce((s, r) => s + (r.total_authorized_budget || 0), 0);
  const totalRemaining = configured.reduce((s, r) => s + (r.budget_remaining || 0), 0);
  const amberCount = rows.filter((r) => r.burn_tone === "amber").length;
  const redCount = rows.filter((r) => r.burn_tone === "red").length;
  const cycleCount = rows.filter((r) => r.cycle_warning === "red").length;

  return (
    <div className="space-y-6" data-testid="admin-budget-tracking">
      <div className="flex items-center gap-3 flex-wrap">
        <div className="h-12 w-12 rounded-md bg-primary/10 text-primary flex items-center justify-center">
          <Wallet className="w-6 h-6" />
        </div>
        <div className="flex-1">
          <p className="text-label">Financials</p>
          <h1 className="font-display text-3xl font-bold tracking-tight">Client Budget Tracking</h1>
          <p className="text-sm text-muted-foreground">
            HHS plan cycle financials per active client · {data?.today || "—"}
          </p>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card className="p-4" data-testid="kpi-total-clients">
          <p className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">Active clients</p>
          <p className="font-display font-bold text-3xl">{data?.total_clients ?? "—"}</p>
          {data?.unconfigured_count > 0 && (
            <p className="text-[11px] text-amber-700 mt-1">{data.unconfigured_count} unconfigured</p>
          )}
        </Card>
        <Card className="p-4" data-testid="kpi-total-authorized">
          <p className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">Total authorized</p>
          <p className="font-display font-bold text-2xl">{fmtCurrency(totalAuthorized)}</p>
          <p className="text-[11px] text-muted-foreground mt-1">Remaining: {fmtCurrency(totalRemaining)}</p>
        </Card>
        <Card className="p-4 border-l-4 border-l-amber-500" data-testid="kpi-amber">
          <p className="text-[10px] uppercase tracking-wider font-semibold text-amber-800">High burn (80%+)</p>
          <p className="font-display font-bold text-3xl text-amber-700">{amberCount}</p>
        </Card>
        <Card className="p-4 border-l-4 border-l-red-500" data-testid="kpi-red">
          <p className="text-[10px] uppercase tracking-wider font-semibold text-red-800">Overrun (95%+) / Cycle ending</p>
          <p className="font-display font-bold text-3xl text-red-700">{redCount + cycleCount}</p>
        </Card>
      </div>

      <Card className="overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-secondary/40">
              <TableHead>Client</TableHead>
              <TableHead>Plan Cycle</TableHead>
              <TableHead className="text-right">Budget Remaining</TableHead>
              <TableHead className="w-[260px]">HHS Days Progress</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Edit</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody data-testid="budget-table">
            {loading && (
              <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-8">Loading…</TableCell></TableRow>
            )}
            {!loading && rows.length === 0 && (
              <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-8">No active clients.</TableCell></TableRow>
            )}
            {rows.map((r) => (
              <TableRow key={r.client_id} data-testid={`budget-row-${r.client_id}`}>
                <TableCell className="font-medium">
                  <div className="font-display font-semibold leading-tight">{r.client_name}</div>
                  <div className="text-[11px] text-muted-foreground">Contractor: {r.contractor_name}</div>
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="text-xs flex items-center gap-1.5">
                      <CalendarDays className="w-3 h-3 text-muted-foreground" />
                      <span>{fmtDate(r.plan_start_date)} → {fmtDate(r.plan_end_date)}</span>
                    </div>
                    <CycleChip warning={r.cycle_warning} days={r.days_until_end} />
                  </div>
                </TableCell>
                <TableCell className="text-right">
                  <div className="font-mono font-semibold" data-testid={`budget-remaining-${r.client_id}`}>
                    {fmtCurrency(r.budget_remaining)}
                  </div>
                  <div className="text-[11px] text-muted-foreground">
                    of {fmtCurrency(r.total_authorized_budget)}
                  </div>
                </TableCell>
                <TableCell>
                  <div className="space-y-1">
                    <ProgressBar pct={r.billed_pct} tone={r.burn_tone} />
                    <div className="flex justify-between text-[11px] font-mono">
                      <span data-testid={`days-billed-${r.client_id}`}>{r.hhs_days_billed_to_date} / {r.total_hhs_days_authorized} days</span>
                      <span className="text-muted-foreground">timeline: {r.timeline_pct}%</span>
                    </div>
                  </div>
                </TableCell>
                <TableCell>
                  <BurnChip tone={r.burn_tone} billedPct={r.billed_pct} />
                </TableCell>
                <TableCell className="text-right">
                  <Button
                    size="sm" variant="ghost"
                    onClick={() => navigate(`/admin/profiles/client/${r.client_id}?tab=budget`)}
                    data-testid={`edit-budget-${r.client_id}`}
                  >
                    <Pencil className="w-3.5 h-3.5 mr-1" /> Edit
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      <p className="text-[11px] text-muted-foreground flex items-center gap-1.5">
        <Activity className="w-3 h-3" />
        Burn-rate logic: <strong>Amber</strong> when days billed ≥ 80% of authorized, <strong>Red</strong> when ≥ 95% or running &gt;15pts ahead of the elapsed plan timeline.
        Plan-cycle chip turns <strong className="text-red-700">red</strong> when within 30 days of plan_end_date.
      </p>
    </div>
  );
}
